import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, Tooltip } from 'recharts';
import { RefreshCw, AlertTriangle, TrendingUp, TrendingDown, CheckCircle2 } from 'lucide-react';
import { puntovService } from '../../../services/api';
import usePolling from '../../../hooks/usePolling';
import { fmtUsd, fmtFecha, fmtPct, fmtEntero, fmtDiaCorto, iso, diasCompletos, horasCompletas, fmtHora, fmtRangoHora } from './formato';
import { PERIODOS, rangoDe, mesDelRango } from './periodos';
import {
  CLAVE_CLIENTES, COLOR_VENTA, leer, guardar, firmaAlerta,
  useAlertasCerradas, useUmbralAlerta, SelectorUmbral, maquinasEnAlerta, maquinasFueraDeServicio,
  BloqueAlertas, AvisosFueraDeServicio, ListaMaquinas, ProductosDelCliente
} from './ventasComun';

// Cada cuánto se vuelven a pedir las ventas. Si el periodo incluye hoy, cada
// 30 s; si es un periodo cerrado las ventas ya no cambian y solo hace falta
// refrescar las alertas de máquinas sin vender, cada 5 min.
const INTERVALO_EN_VIVO_MS = 30 * 1000;
const INTERVALO_CERRADO_MS = 5 * 60 * 1000;
const PANELES = 3;

// En los indicadores, desde 100.000 el monto se abrevia ("USD 175,9 mil")
// para que quepa en una línea; el valor exacto queda en el title.
const fmtUsdCorto = (v) => (Math.abs(Number(v)) >= 100000
  ? `USD ${(Number(v) / 1000).toLocaleString('es-VE', { maximumFractionDigits: 1 })} mil`
  : fmtUsd(v));

const Kpi = ({ titulo, valor, exacto, detalle }) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{titulo}</div>
    <div className="text-base font-semibold text-gray-900 tabular-nums whitespace-nowrap truncate" title={exacto || valor}>{valor}</div>
    {detalle && <div className="text-[11px] text-gray-500 tabular-nums truncate">{detalle}</div>}
  </div>
);

// Una fila del panel (son 7: encabezado + 6 secciones). En pantallas grandes
// las filas de los tres paneles comparten altura (subgrid), así cada sección
// empieza a la misma altura aunque un panel tenga más contenido que otro.
const Fila = ({ children, ultima = false }) => (
  <div className={`min-w-0 px-4 ${children ? 'pt-4' : ''} ${ultima ? 'pb-4' : ''}`}>{children}</div>
);

const TooltipDia = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-2.5 py-1.5 text-xs">
      <div className="font-semibold text-gray-800">{fmtFecha(d.dia)}</div>
      <div className="text-gray-700 tabular-nums">{fmtUsd(d.venta)} · {fmtEntero(d.n_ventas)} ventas</div>
    </div>
  );
};

const TooltipHora = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const h = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-2.5 py-1.5 text-xs">
      <div className="font-semibold text-gray-800">{fmtRangoHora(h.hora)}</div>
      <div className="text-gray-700 tabular-nums">{fmtUsd(h.venta)} · {fmtEntero(h.n_ventas)} ventas</div>
    </div>
  );
};

// Bloque destacado: la venta de hoy (con la de ayer al lado) si el periodo
// incluye hoy; si no, el total del periodo.
const BloqueDestacado = ({ datos, incluyeHoy }) => {
  if (!incluyeHoy) {
    return (
      <div className="rounded-lg bg-blue-50/60 border border-blue-100 px-3 py-2.5">
        <div className="text-[11px] uppercase tracking-wide text-blue-700 font-medium">Venta del periodo</div>
        <div className="text-2xl font-bold text-gray-900 tabular-nums">{fmtUsd(datos.totales.venta)}</div>
        <div className="text-xs text-gray-500 tabular-nums">{fmtEntero(datos.totales.n_ventas)} ventas · {fmtEntero(datos.totales.unidades)} unidades</div>
      </div>
    );
  }

  const ahora = new Date();
  const claveAyer = iso(new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() - 1));
  const hoy = datos.por_dia.find(d => d.dia === iso(ahora));
  const ayer = datos.por_dia.find(d => d.dia === claveAyer);
  const ventaHoy = hoy?.venta ?? 0;
  const ventaAyer = ayer?.venta ?? 0;
  const ayerEnRango = claveAyer >= datos.fecha_desde;
  const sube = ventaHoy >= ventaAyer;

  return (
    <div className="rounded-lg bg-blue-50/60 border border-blue-100 px-3 py-2.5">
      <div className="text-[11px] uppercase tracking-wide text-blue-700 font-medium">Hoy</div>
      <div className="flex items-end justify-between gap-2">
        <div className="text-2xl font-bold text-gray-900 tabular-nums">{fmtUsd(ventaHoy)}</div>
        {ayerEnRango && (
          <div className={`flex items-center gap-1 text-xs tabular-nums ${sube ? 'text-green-700' : 'text-gray-500'}`}>
            {sube ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
            Ayer {fmtUsd(ventaAyer)}
          </div>
        )}
      </div>
      <div className="text-xs text-gray-500 tabular-nums">{fmtEntero(hoy?.n_ventas)} ventas</div>
    </div>
  );
};

const PanelCliente = ({ base, clientes, ocupados, onCambiar, resultado, mesMensualidad, incluyeHoy, ocultas, onOcultar, onMostrar }) => {
  const [mensualidad, setMensualidad] = useState(null);

  // La mensualidad cambia a lo sumo una vez al día: se pide al elegir el
  // cliente o el periodo, no en cada vuelta del polling.
  useEffect(() => {
    if (!base || !mesMensualidad) { setMensualidad(null); return undefined; }
    let vigente = true;
    puntovService.mensualidad(base, mesMensualidad)
      .then(res => { if (vigente) setMensualidad(res.data); })
      .catch(() => { if (vigente) setMensualidad(null); });
    return () => { vigente = false; };
  }, [base, mesMensualidad]);

  const datos = resultado?.datos;
  const t = datos?.totales;
  const unSoloDia = datos && datos.fecha_desde === datos.fecha_hasta;
  const serie = useMemo(
    () => (datos ? diasCompletos(datos.por_dia, datos.fecha_desde, datos.fecha_hasta) : []),
    [datos]
  );
  const enAlerta = useMemo(() => maquinasEnAlerta(datos), [datos]);
  const alertasVisibles = enAlerta.filter(m => !ocultas.has(firmaAlerta(base, m)));
  const activas = datos ? datos.por_maquina.filter(m => m.activa).length : 0;
  const fueraDeServicio = datos ? datos.por_maquina.filter(m => m.fuera_de_servicio).length : 0;
  const enMora = mensualidad?.estado === 'emitido' && mensualidad.cobro?.estado === 'en_mora';
  const borde = alertasVisibles.length ? 'border-amber-300' : 'border-gray-200';

  let estado = null;
  if (!base) {
    estado = (
      <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center text-sm text-gray-500">
        Elija un cliente para ver sus ventas.
      </div>
    );
  } else if (resultado?.error) {
    estado = (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-xs flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
        <span>{resultado.error}{datos ? ' Se muestra el último dato recibido.' : ''}</span>
      </div>
    );
  } else if (!datos) {
    estado = (
      <div className="py-12">
        <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  let grafico = null;
  if (datos && t.n_ventas === 0) {
    grafico = (
      <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center text-gray-500">
        <TrendingUp size={20} className="mx-auto mb-1 text-gray-300" />
        <p className="text-xs">Sin ventas aprobadas en este periodo.</p>
      </div>
    );
  } else if (datos && unSoloDia) {
    grafico = (
      <div>
        <div className="text-xs font-medium text-gray-600 mb-1">Venta por hora</div>
        <div className="h-24">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={horasCompletas(datos.por_hora)} margin={{ top: 4, right: 4, bottom: 0, left: 4 }} barCategoryGap="15%">
              <XAxis dataKey="hora" tickFormatter={fmtHora} tick={{ fontSize: 10, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} interval={5} />
              <Tooltip content={<TooltipHora />} cursor={{ fill: '#f3f4f6' }} />
              <Bar dataKey="venta" fill={COLOR_VENTA} radius={[2, 2, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  } else if (datos) {
    grafico = (
      <div>
        <div className="text-xs font-medium text-gray-600 mb-1">Venta por día</div>
        <div className="h-24">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={serie} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <XAxis dataKey="dia" tickFormatter={fmtDiaCorto} tick={{ fontSize: 10, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} minTickGap={28} />
              <Tooltip content={<TooltipDia />} cursor={{ stroke: '#9ca3af', strokeDasharray: '3 3' }} />
              <Area type="linear" dataKey="venta" stroke={COLOR_VENTA} strokeWidth={2} fill={COLOR_VENTA} fillOpacity={0.12} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border ${borde} shadow-sm min-w-0 flex flex-col lg:grid lg:grid-rows-subgrid lg:row-[span_7]`}>
      <div className="p-4 pb-3 border-b border-gray-100 space-y-2">
        <select
          value={base}
          onChange={(e) => onCambiar(e.target.value)}
          className="w-full h-9 px-2.5 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">Elegir cliente…</option>
          {clientes.map(c => (
            <option key={c.base_datos} value={c.base_datos} disabled={c.base_datos !== base && ocupados.includes(c.base_datos)}>
              {c.slug}
            </option>
          ))}
        </select>
        {datos && (
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="text-gray-500">
              {datos.por_maquina.length} máquinas · {activas} activas
              {fueraDeServicio > 0 && ` · ${fueraDeServicio} fuera de servicio`}
            </span>
            <span className="ml-auto" />
            {enAlerta.length > 0
              ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">{enAlerta.length} sin vender</span>
              : activas > fueraDeServicio && <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium flex items-center gap-1"><CheckCircle2 size={11} /> Todas vendiendo</span>}
            {enMora && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">Mensualidad en mora</span>}
          </div>
        )}
      </div>

      {/* Filas 2 a 7. Siempre se renderizan (vacías si no aplican) para que
          la misma sección quede a la misma altura en los tres paneles. */}
      <Fila>
        {estado || (datos && <BloqueDestacado datos={datos} incluyeHoy={incluyeHoy} />)}
      </Fila>

      <Fila>
        {datos && (
          <div className="grid grid-cols-3 gap-3">
            {unSoloDia
              ? <Kpi titulo="Unidades" valor={fmtEntero(t.unidades)} detalle="Productos vendidos" />
              : incluyeHoy
                ? <Kpi titulo="Periodo" valor={fmtUsdCorto(t.venta)} exacto={fmtUsd(t.venta)} detalle={`${fmtEntero(t.n_ventas)} ventas`} />
                : <Kpi titulo="Margen" valor={fmtPct(t.margen_pct)} detalle="Bruto" />}
            <Kpi
              titulo="Ganancia"
              valor={fmtUsdCorto(t.ganancia)}
              exacto={fmtUsd(t.ganancia)}
              detalle={incluyeHoy ? `Margen ${fmtPct(t.margen_pct)}` : `Costo ${fmtUsdCorto(t.costo)}`}
            />
            <Kpi titulo="Ticket" valor={t.ticket_promedio === null ? '—' : fmtUsd(t.ticket_promedio)} detalle="Promedio" />
          </div>
        )}
      </Fila>

      <Fila>
        {datos && enAlerta.length > 0 && (
          <BloqueAlertas base={base} datos={datos} ocultas={ocultas} onOcultar={onOcultar} onMostrar={onMostrar} />
        )}
      </Fila>

      <Fila>{grafico}</Fila>

      <Fila>
        {datos && datos.por_maquina.length > 0 && <ListaMaquinas maquinas={datos.por_maquina} />}
      </Fila>

      <Fila ultima>
        {datos && (datos.por_producto?.length > 0 || datos.maquinas_sin_tablas?.length > 0) && (
          <div className="space-y-4">
            <ProductosDelCliente productos={datos.por_producto} />
            {datos.maquinas_sin_tablas?.length > 0 && (
              <p className="text-[11px] text-amber-700 flex items-start gap-1">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                {datos.maquinas_sin_tablas.length === 1 ? '1 máquina no se incluye' : `${datos.maquinas_sin_tablas.length} máquinas no se incluyen`}
                {' '}(ver detalle en la pestaña Ventas).
              </p>
            )}
          </div>
        )}
      </Fila>
    </div>
  );
};

// Tres clientes lado a lado con las ventas del periodo elegido. Si el
// periodo incluye hoy se actualiza cada 30 s mientras la pestaña está visible.
const MonitorVentas = () => {
  const [clientes, setClientes] = useState([]);
  const [paneles, setPaneles] = useState(() => Array(PANELES).fill(''));
  const [resultados, setResultados] = useState({});
  const [errorClientes, setErrorClientes] = useState('');
  // El monitor siempre abre en "Hoy".
  const [periodo, setPeriodo] = useState('hoy');
  const [rango, setRango] = useState(() => rangoDe('hoy'));
  const { ocultas, ocultar: ocultarAlertas, mostrar: mostrarAlertas } = useAlertasCerradas();
  const [umbral, elegirUmbral] = useUmbralAlerta();

  const [desde, hasta] = periodo === 'rango' ? rango : rangoDe(periodo);
  const rangoValido = Boolean(desde && hasta && desde <= hasta);
  const incluyeHoy = rangoValido && hasta >= iso(new Date());
  const clave = (b) => `${b}|${desde}|${hasta}`;

  // Los resultados se guardan por cliente y rango: una respuesta que llega
  // tarde (de otro cliente u otro periodo) no pisa lo que se está viendo.
  const consultar = useCallback(async (bases, d, h, alertaMin) => {
    if (!bases.length || !d || !h || d > h) return;
    const respuestas = await Promise.allSettled(bases.map(b => puntovService.ventas(b, d, h, { alertaMin })));
    setResultados(prev => {
      const siguiente = { ...prev };
      respuestas.forEach((r, i) => {
        const k = `${bases[i]}|${d}|${h}`;
        siguiente[k] = r.status === 'fulfilled'
          ? { datos: r.value.data, error: '' }
          : { datos: prev[k]?.datos || null, error: r.reason?.message || 'No se pudieron consultar las ventas.' };
      });
      return siguiente;
    });
    if (respuestas.every(r => r.status === 'rejected')) {
      throw new Error('Fallaron todas las consultas');
    }
  }, []);

  // Paneles iniciales: los guardados en este navegador y, si faltan, los
  // primeros clientes de la lista.
  useEffect(() => {
    puntovService.listarClientes()
      .then(res => {
        const conBase = (res.data || []).filter(c => c.base_existe !== false && !c.archivado);
        setClientes(conBase);
        const validas = new Set(conBase.map(c => c.base_datos));
        const guardados = leer(CLAVE_CLIENTES, []).filter(b => validas.has(b));
        const resto = conBase.map(c => c.base_datos).filter(b => !guardados.includes(b));
        setPaneles([...guardados, ...resto, ...Array(PANELES).fill('')].slice(0, PANELES));
      })
      .catch(err => setErrorClientes(err.message || 'No se pudieron cargar los clientes'));
  }, []);

  const basesActivas = useMemo(() => [...new Set(paneles.filter(Boolean))], [paneles]);
  const tarea = useCallback(() => consultar(basesActivas, desde, hasta, umbral), [consultar, basesActivas, desde, hasta, umbral]);
  const { ultimaActualizacion, actualizando, fallos, refrescar } = usePolling(
    tarea,
    incluyeHoy ? INTERVALO_EN_VIVO_MS : INTERVALO_CERRADO_MS
  );

  // Al cambiar de cliente, de periodo o el tiempo de alerta se consulta
  // enseguida, sin esperar al siguiente turno del polling.
  const claveConsulta = `${basesActivas.join(',')}|${desde}|${hasta}|${umbral}`;
  // usePolling ya consulta al montar: solo se refresca cuando la clave cambia.
  const claveAnterior = useRef(claveConsulta);
  useEffect(() => {
    if (claveAnterior.current === claveConsulta) return;
    claveAnterior.current = claveConsulta;
    refrescar();
  }, [claveConsulta, refrescar]);

  const cambiarPanel = (indice, base) => {
    const siguientes = paneles.map((b, i) => (i === indice ? base : b));
    setPaneles(siguientes);
    guardar(CLAVE_CLIENTES, siguientes);
  };

  const elegirPeriodo = (valor) => {
    if (valor === 'rango') setRango([desde, hasta]);
    setPeriodo(valor);
  };

  // Firmas de todas las alertas vigentes en pantalla, para contar las
  // visibles y las cerradas.
  const firmasEnAlerta = basesActivas.flatMap(b => (resultados[clave(b)]?.datos?.por_maquina || [])
    .filter(m => m.alerta_sin_venta)
    .map(m => firmaAlerta(b, m)));
  const firmasCerradas = firmasEnAlerta.filter(f => ocultas.has(f));
  const totalAlertas = firmasEnAlerta.length - firmasCerradas.length;
  const claseControl = 'h-9 px-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none';
  const diasFuera = basesActivas.map(b => resultados[clave(b)]?.datos?.dias_fuera_de_servicio).find(Boolean);
  const gruposFueraDeServicio = basesActivas.map(b => ({
    base: b,
    nombre: clientes.find(c => c.base_datos === b)?.slug || b.replace(/^esgamanc_/, ''),
    maquinas: maquinasFueraDeServicio(resultados[clave(b)]?.datos)
  }));
  const resumenFueraDeServicio = diasFuera ? `${diasFuera} días` : 'el plazo configurado';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Periodo</span>
          <select value={periodo} onChange={(e) => elegirPeriodo(e.target.value)} className={claseControl}>
            {PERIODOS.map(p => <option key={p.id} value={p.id}>{p.etiqueta}</option>)}
          </select>
        </label>
        {periodo === 'rango' && (
          <>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Desde</span>
              <input type="date" value={desde} max={hasta} onChange={(e) => setRango([e.target.value, hasta])} className={claseControl} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Hasta</span>
              <input type="date" value={hasta} min={desde} onChange={(e) => setRango([desde, e.target.value])} className={claseControl} />
            </label>
          </>
        )}
        <SelectorUmbral umbral={umbral} onCambiar={elegirUmbral} className={claseControl} />
        <div className="flex items-center gap-3 ml-auto">
          <span className="text-xs text-gray-500 tabular-nums flex items-center gap-1.5">
            {fallos > 0
              ? <span className="text-amber-700">Sin conexión con Punto V, reintentando…</span>
              : ultimaActualizacion && basesActivas.length > 0 && (
                incluyeHoy ? (
                  <>
                    <span className="relative flex w-2 h-2">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-green-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex w-2 h-2 rounded-full bg-green-500" />
                    </span>
                    En vivo · {ultimaActualizacion.toLocaleTimeString('es-VE')}
                  </>
                ) : `Periodo cerrado · ${ultimaActualizacion.toLocaleTimeString('es-VE')}`
              )}
          </span>
          <AvisosFueraDeServicio grupos={gruposFueraDeServicio} dias={diasFuera} />
          <button onClick={refrescar} disabled={actualizando || basesActivas.length === 0 || !rangoValido} className="h-9 px-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-60">
            <RefreshCw size={15} className={actualizando ? 'animate-spin' : ''} /> Actualizar
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="text-xs text-gray-500">
          {rangoValido ? `${fmtFecha(desde)} a ${fmtFecha(hasta)}` : 'Rango no válido: "desde" debe ser anterior o igual a "hasta"'}
          {' '}· montos en USD · {incluyeHoy ? 'se actualiza cada 30 s' : 'las alertas se revisan cada 5 min'}
        </p>
        {firmasEnAlerta.length > 0 && (
          <p className="text-sm font-medium text-amber-800 flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <AlertTriangle size={15} />
            {firmasEnAlerta.length === 1 ? '1 máquina sin vender' : `${firmasEnAlerta.length} máquinas sin vender`} en los clientes que está viendo
            {firmasCerradas.length > 0 && totalAlertas > 0 && (
              <span className="flex items-center gap-1 font-semibold text-red-700">
                ·
                <span className="w-2 h-2 rounded-full bg-red-600" />
                {totalAlertas === 1 ? '1 nueva' : `${totalAlertas} nuevas`}
              </span>
            )}
            {firmasCerradas.length > 0 && (
              <button onClick={() => mostrarAlertas(firmasEnAlerta)} className="font-medium text-blue-700 hover:underline">
                Mostrar todas
              </button>
            )}
          </p>
        )}
      </div>

      {errorClientes && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> <span>{errorClientes}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-y-0">
        {paneles.map((base, i) => (
          <PanelCliente
            key={i}
            base={base}
            clientes={clientes}
            ocupados={paneles}
            onCambiar={(b) => cambiarPanel(i, b)}
            resultado={!base ? null : rangoValido ? resultados[clave(base)] : { datos: null, error: 'Elija un rango de fechas válido.' }}
            mesMensualidad={mesDelRango(desde, hasta)}
            incluyeHoy={incluyeHoy}
            ocultas={ocultas}
            onOcultar={ocultarAlertas}
            onMostrar={mostrarAlertas}
          />
        ))}
      </div>

      <p className="text-xs text-gray-400">
        Fuente: Punto V, solo ventas aprobadas (la misma consulta de la pestaña Ventas). Una máquina activa sale en
        alerta cuando se detiene: pasa el tiempo configurado sin una venta aprobada (en rojo si pasa de un día). Si
        lleva más de {resumenFueraDeServicio} sin vender o nunca ha vendido, se marca fuera de servicio y no alerta. Las alertas
        cerradas quedan recogidas; una máquina cuenta una sola vez y solo se marcan como nuevas las que no se habían cerrado.
      </p>
    </div>
  );
};

export default MonitorVentas;

